/**
 * Chat Feature - API Layer
 *
 * Handles all API calls related to chat functionality
 */

import { api } from '../../../lib/api';
import type { APIResponse } from '@room/shared-ui';
import type { ChatMessage, ChatSummary } from '../types';

export type ChatsResponse = {
  chats: ChatSummary[];
};

export type MessagesResponse = {
  messages: ChatMessage[];
};

export type SendMessageResponse = {
  message: ChatMessage;
};

/**
 * Fetch all chats for the current user
 */
export async function fetchChats(): Promise<ChatSummary[]> {
  const response = await api.get<APIResponse<ChatsResponse>>('/chats');
  if (!response.success) {
    throw new Error(response.error || 'Failed to load chats');
  }
  return response.data?.chats ?? [];
}

/**
 * Fetch messages for a specific chat
 */
export async function fetchMessages(chatId: string): Promise<ChatMessage[]> {
  const response = await api.get<APIResponse<MessagesResponse>>(
    `/chats/${chatId}/messages`
  );
  if (!response.success) {
    throw new Error(response.error || 'Failed to load messages');
  }
  return response.data?.messages ?? [];
}

/**
 * Send a message to a chat
 */
export async function sendMessage(
  chatId: string,
  content: string
): Promise<ChatMessage> {
  const response = await api.post<APIResponse<SendMessageResponse>>(
    `/chats/${chatId}/messages`,
    { content }
  );
  if (!response.success || !response.data?.message) {
    throw new Error(response.error || 'Unable to send message');
  }
  return response.data.message;
}
